'use client'

import React from 'react'
import styled, {css} from 'styled-components'

import theme, {ThemeColor} from '@/src/config/theme'

import {NextLink} from '@/src/shared/ui/next-link'

export interface ILinkProps {
  $dashed?: boolean
  ref?: unknown
}

export const linkColors = ({
  props,
  color = theme.colors.link,
  hoverColor = theme.colors.primary,
  borderColor = theme.colors.link,
  borderHoverColor = theme.colors.primary,
  darkColor = theme.colors.gray6,
  darkHoverColor = theme.colors.gray6,
  borderColorDark = theme.colors.primary,
  borderHoverColorDark = theme.colors.primary,
}: {
  props: ILinkProps
  color?: ThemeColor
  hoverColor?: ThemeColor
  borderColor?: ThemeColor
  borderHoverColor?: ThemeColor
  darkColor?: ThemeColor
  darkHoverColor?: ThemeColor
  borderColorDark?: ThemeColor
  borderHoverColorDark?: ThemeColor
}) => {
  return css`
    color: ${color};

    span {
      border-bottom-color: ${(props.$dashed && borderColor) || 'unset'};
    }

    &:hover,
    &:active {
      color: ${hoverColor};

      span {
        border-bottom-color: ${(props.$dashed && borderHoverColor) || 'unset'};
      }
    }

    [data-dark='true'] & {
      color: ${darkColor};

      span {
        border-bottom: 1px dashed ${(props.$dashed && borderColorDark) || 'unset'};
      }

      &:hover,
      &:active {
        color: ${darkHoverColor};

        span {
          border-bottom-color: ${(props.$dashed && borderHoverColorDark) || 'unset'};
          color: ${darkHoverColor};
        }
      }
    }
  `
}

export interface IProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  icon?: React.ReactNode
  iconAfter?: React.ReactNode
  href?: string
  asChild?: boolean
  child?: React.ReactNode
}

const A = styled.a``

const NextLink_ = ({children, href = undefined, icon, iconAfter, asChild, className, child, ...other}: IProps) => {
  const Component: typeof A | typeof NextLink = (href && NextLink) || A
  let options: {[key: string]: any} = {className}

  if (href) {
    options = options as {className: string; href: string; prefetch?: boolean}
    options.href = href
  }

  return (
    // @ts-ignore
    <Component className={options.className} href={options.href} {...other}>
      {(asChild && (child || children)) || (
        <>
          {icon}
          <span>{child || children}</span>
          {iconAfter}
        </>
      )}
    </Component>
  )
}

export const Link = styled(NextLink_)<ILinkProps>`
  color: ${theme.colors.text};
  font-size: 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  //font-weight: 450;

  span {
    display: inline-block;

    border-bottom: 1px dashed ${props => (props.$dashed && props.theme.colors.text) || 'unset'};
    line-height: 1;
  }

  ${props => linkColors({props})}

  ${props =>
    !props.asChild &&
    css`
      display: inline-flex;
      gap: 5px;
    `}
`
